import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';

import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { CreateSkillDocument } from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { SkillForm } from 'skill/components/SkillForm';
import { TSkillFormValues } from 'skill/types';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';

interface IProps extends IPageDrawerProps {
    onCreate?(skillId?: string, formValues?: TSkillFormValues): void;
    onClose?(): void;
}

export const SkillCreateDrawer = ({
    open,
    onCreate,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [createSkill, { loading: createSkillLoading }] =
        useMutation(CreateSkillDocument);
    const { canCreate = false } = useAppPermissions('skill', 'management');

    if (!canCreate) return <FourOFourPage />;

    const handleSubmit = async (values: TSkillFormValues) => {
        const { groups, certificates, thumbnail, ...otherValues } = values;

        const skillGroups = groups?.map((req) => ({
            groupId: req.group.id,
            minAbsolute: req.minAbsolute
                ? parseInt(String(req.minAbsolute), 10)
                : null,
            minPercent: req.minPercent
                ? parseInt(String(req.minPercent), 10)
                : null,
        }));
        const skillCertificates = certificates?.map((certificate) => ({
            id: certificate.id,
            isMandatory: !!certificate.isMandatory,
        }));

        try {
            const response = await createSkill({
                variables: {
                    skill: {
                        groups: skillGroups,
                        certificates: skillCertificates,
                        thumbnailId: thumbnail?.id || undefined,
                        ...otherValues,
                    },
                },
                update: (cache) => {
                    cache.evict({ fieldName: 'skills' });
                },
            });

            const skillId = response.data?.createSkill?.skill?.id;

            onCreate?.(skillId, values);
        } catch (error) {
            showApolloError(error);

            return;
        }

        displaySnackbar(
            translate('skillsManagementPage.actionSuccess.create'),
            {
                variant: 'success',
            }
        );

        onClose?.();

        return;
    };

    const initialValues: TSkillFormValues = {
        name: '',
        shortName: '',
        description: '',
    };

    return (
        <PageDrawer open={open} onClose={onClose} {...other}>
            <Box sx={{ p: { xs: 2, sm: 4 }, pt: { xs: 0, sm: 0 } }}>
                <SkillForm
                    disabled={createSkillLoading}
                    initialValues={initialValues}
                    onClose={onClose}
                    onSubmit={handleSubmit}
                />
            </Box>
        </PageDrawer>
    );
};
